import { Button, Card, Col, Row, Space, Typography, Tag, Statistic, Divider } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  ApiTwoTone,
  CheckCircleTwoTone,
  ExperimentTwoTone,
  HistoryOutlined,
  RadarChartOutlined,
  ShareAltOutlined,
  CloudTwoTone,
} from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useEnvLabel } from '../util/useEnvLabel.js';
import { tour } from '@digiwedge/tour-react';

const { Title, Paragraph } = Typography;

export default function HomePage() {
  const nav = useNavigate();
  const env = useEnvLabel();
  const im = (import.meta as unknown as { env?: { VITE_REGISTRY_URL?: string } }) || {};
  const registryUrl = String(im.env?.VITE_REGISTRY_URL || 'http://localhost:3410');
  const [stats, setStats] = useState<{
    sites?: number;
    vendors?: number;
    overrides?: number;
  } | null>(null);

  useEffect(() => {
    const url = `${registryUrl.replace(/\/$/, '')}/health/stats`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((j) => setStats(j))
      .catch(() => setStats(null));
  }, [registryUrl]);

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Hero */}
      <div
        style={{
          padding: '32px 24px',
          borderRadius: 12,
          background:
            'linear-gradient(135deg, rgba(24,144,255,0.12) 0%, rgba(235,47,150,0.10) 100%)',
          border: '1px solid rgba(0,0,0,0.06)',
          marginBottom: 16,
        }}
      >
        <Space direction="vertical" size={8} style={{ display: 'flex' }}>
          <Space align="center" size={12}>
            <Title level={2} style={{ margin: 0 }}>
              CMP Admin Portal
            </Title>
            <Tag color={env.color}>{env.label}</Tag>
          </Space>
          <Paragraph style={{ maxWidth: 720, margin: 0, fontSize: 16 }}>
            Coordinate consent, public scans, diagnostics, and guided onboarding from a single
            workspace. Sign in for admin tooling or explore the shared utilities that stay open to
            everyone.
          </Paragraph>
          <Space size={12} wrap style={{ marginTop: 8 }}>
            <Button
              id="btn-login"
              className="btn-start"
              type="primary"
              size="large"
              onClick={() => nav('/login')}
            >
              Login to Admin
            </Button>
            <Button id="btn-health" size="large" onClick={() => nav('/health')}>
              View Health
            </Button>
            <a href="/docs" target="_blank" rel="noreferrer">
              <Button size="large" type="dashed">
                Read Docs
              </Button>
            </a>
            <Button
              size="large"
              type="primary"
              icon={<RadarChartOutlined />}
              onClick={() => nav('/scanner')}
            >
              Explore Public Scans
            </Button>
            <a href="/scanner-tool" target="_blank" rel="noreferrer">
              <Button size="large" type="dashed" icon={<RadarChartOutlined />}>
                Scanner Tool
              </Button>
            </a>
            <a href="/scan-api" target="_blank" rel="noreferrer">
              <Button size="large" type="dashed" icon={<CloudTwoTone />}>
                Scan API
              </Button>
            </a>
            <Button
              size="large"
              type="default"
              onClick={() => {
                const loginBtn = document.querySelector('#btn-login');
                const healthBtn = document.querySelector('#btn-health');
                if (!loginBtn || !healthBtn) return;
                const steps = [
                  { id: 'start', target: loginBtn, content: <>Click here to begin login</> },
                  { id: 'health', target: healthBtn, content: <>Check system health here</> },
                ];
                tour.start('onboarding-v1', steps, {
                  continuous: true,
                  storageKey: 'portal.onboarding.v1',
                });
              }}
            >
              Start Tour
            </Button>
          </Space>
        </Space>
      </div>

      {/* Feature tags */}
      <div style={{ marginBottom: 12 }}>
        <Space size={[8, 8]} wrap>
          <Tag color="geekblue">Baseline comparisons</Tag>
          <Tag color="purple">Shareable scan history</Tag>
          <Tag color="cyan">Consent-aware analytics</Tag>
          <Tag color="magenta">Tour & experiment telemetry</Tag>
          <Tag color="green">Workspace-scoped API tokens</Tag>
        </Space>
      </div>

      {/* Stats */}
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="Sites managed"
              value={typeof stats?.sites === 'number' ? stats?.sites : '—'}
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="Vendors classified"
              value={typeof stats?.vendors === 'number' ? stats?.vendors : '—'}
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="Configured overrides"
              value={typeof stats?.overrides === 'number' ? stats?.overrides : '—'}
            />
          </Card>
        </Col>
      </Row>

      {/* Highlights */}
      <Row gutter={[12, 12]}>
        <Col xs={24} md={8}>
          <Card>
            <Space direction="vertical">
              <HistoryOutlined style={{ fontSize: 24, color: '#1890ff' }} />
              <Title level={4} style={{ margin: 0 }}>
                Public Scans Workspace
              </Title>
              <Paragraph style={{ margin: 0 }}>
                Track scanner history, diff against saved baselines, and copy deep links for
                stakeholders without exposing admin credentials.
              </Paragraph>
              <Button type="link" icon={<ShareAltOutlined />} onClick={() => nav('/scanner')}>
                Open scans center
              </Button>
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Space direction="vertical">
              <ExperimentTwoTone twoToneColor="#722ed1" style={{ fontSize: 24 }} />
              <Title level={4} style={{ margin: 0 }}>
                Consent & Analytics
              </Title>
              <Paragraph style={{ margin: 0 }}>
                Run consent-aware telemetry for tours and experiments — data only streams when
                analytics consent is granted.
              </Paragraph>
              <Button
                type="link"
                icon={<ApiTwoTone twoToneColor="#1890ff" />}
                onClick={() => nav('/diagnostics')}
              >
                View diagnostics
              </Button>
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Space direction="vertical">
              <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: 24 }} />
              <Title level={4} style={{ margin: 0 }}>
                Operations & Overrides
              </Title>
              <Paragraph style={{ margin: 0 }}>
                Publish configs, manage feature flags, and apply tenant overrides with guardrails
                that keep workspace dependencies in sync.
              </Paragraph>
              <Button type="link" onClick={() => nav('/config')}>
                Go to configuration
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>

      <Divider style={{ margin: '24px 0 12px' }}>Latest updates</Divider>

      <Row gutter={[12, 12]}>
        <Col xs={24} md={16}>
          <Card>
            <Title level={4} style={{ marginTop: 0 }}>
              What&apos;s new in this workspace
            </Title>
            <Paragraph style={{ marginBottom: 8 }}>
              The portal picked up several UX and observability upgrades recently:
            </Paragraph>
            <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
              <li>
                Public scans now maintain clipboard-friendly permalinks, history snapshots, and
                resilient exports for large payloads.
              </li>
              <li>
                API helpers run with injectable access tokens, keeping registry calls secure across
                workspaces and simplifying local testing.
              </li>
              <li>
                Tour analytics streams through consent-aware emitters, so experiment metrics only
                flow when the visitor opts in.
              </li>
            </ul>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Title level={4} style={{ marginTop: 0 }}>
              Need a guided start?
            </Title>
            <Paragraph style={{ marginBottom: 12 }}>
              Launch the in-product tour to see how scanning, overrides, and reporting tie together
              for a new tenant rollout.
            </Paragraph>
            <Button block type="primary" onClick={() => nav('/scanner')}>
              Start with scans
            </Button>
          </Card>
        </Col>
      </Row>
    </Space>
  );
}
